/**
 * Constantes centralizadas para el módulo de Generación de Guías
 * 
 * Propósito: Eliminar magic strings/IDs y valores hardcodeados,
 * mejorar mantenibilidad y permitir tree-shaking.
 * 
 * NOTA: Todas las constantes son inmutables (Object.freeze)
 */

/**
 * IDs de unidades de medida del sistema
 * Estos IDs provienen de la base de datos MongoDB
 */
export const UNIT_IDS = Object.freeze({
	KILOGRAM: '662efd278ddfdcb64699a9b0',
	KG: '662efd278ddfdcb64699a9b0', // Alias para compatibilidad
	UNIT: '662efd278ddfdcb64699a9b1',
});

/**
 * Códigos de tipos de archivo para el gestor documental
 */
export const FILE_TYPE_CODES = Object.freeze({
	GUIA_DESPACHO: 'GD',
	FACTURA: 'FV',
	NOTA_CREDITO: 'NC',
	NOTA_DEBITO: 'ND',
});

/**
 * Configuración de polling para esperar generación de PDFs
 */
export const PDF_POLL_CONFIG = Object.freeze({
	INTERVAL_MS: 3000,
	MAX_ATTEMPTS: 20,
	TIMEOUT_MS: 60000, // 20 attempts * 3 seconds = 60 segundos
});

/**
 * Fases del workflow de procesamiento de guías
 */
export const WORKFLOW_PHASES = Object.freeze({
	PROCESSING: 'processing',
	ROLLBACK: 'rollback',
	COMPLETED: 'completed',
	FAILED: 'failed',
	ABORTED: 'aborted',
});

/**
 * Estados de los pasos del workflow
 */
export const STEP_STATUSES = Object.freeze({
	PENDING: 'pending',
	LOADING: 'loading',
	DONE: 'done',
	ERROR: 'error',
	WARNING: 'warning',
	SKIPPED: 'skipped',
});

/**
 * Categorías de guías soportadas
 */
export const GUIDE_CATEGORIES = Object.freeze({
	EPP: 'epp',
	RESIDUOS: 'residuos',
});

/**
 * Tipos de operación EPP
 */
export const EPP_OPERATION_TYPES = Object.freeze({
	RETIRO: 'retiro',
	TRANSFERENCIA: 'transferencia',
});

/**
 * Tipos de operación Residuos
 */
export const RESIDUOS_OPERATION_TYPES = Object.freeze({
	PELIGROSOS: 'peligrosos',
	NO_PELIGROSOS: 'no_peligrosos',
	LIMPIEZA_PLAYA: 'limpieza_playa',
	ENVIO_BODEGA_PELIGROSOS: 'envio_bodega_peligrosos',
	ENVIO_BODEGA_NO_PELIGROSOS: 'envio_bodega_no_peligrosos',
});

/**
 * IDs de tipos de establecimiento que son centros de cultivo
 */
export const CULTIVATION_CENTER_TYPE_IDS = Object.freeze([
	'666b178f30211c8bafe21b79', // SWE - Centro de Agua Mar
	'666b179430211c8bafe21b7a', // FWE - Centro de Agua Dulce
	'6764837ecf57020e3c0b7b31', // LWE - Centro de Lago
]);

/**
 * ID del tipo de establecimiento "Planta Primaria"
 */
export const PRIMARY_PLANT_TYPE_ID = '69c58ab318ec682fafbf6317'; // PPP

/**
 * Tipos de establecimiento que habilitan Mortalidad Entera (PECES_ENTEROS)
 */
export const PECES_ENTEROS_ELIGIBLE_TYPE_IDS = Object.freeze([
	...CULTIVATION_CENTER_TYPE_IDS,
	PRIMARY_PLANT_TYPE_ID,
]);

/**
 * Tipos de destino para residuos
 */
export const DESTINY_TYPES = Object.freeze({
	BODEGA_TRANSITORIA: '1',
	DISPOSICION_FINAL: '2',
});

/**
 * Labels amigables para campos del formulario de guías
 * Centralizados para evitar recreación en cada render
 */
export const FIELD_LABELS = Object.freeze({
	// GuideCategoryBar
	'guide_category': 'Categoría de guía',
	'epp_operation_type': 'Tipo de operación EPP',
	'residuos_operation_type': 'Tipo de residuo',

	// OrigenSection
	'establishment_id': 'Establecimiento de origen',
	'company_rut': 'RUT emisor',
	'company_name': 'Razón social emisor',
	'establishment_address': 'Dirección establecimiento',
	'responsible_id': 'Responsable',

	// DestinoSection (EPP)
	'establishment_destination_id': 'Establecimiento de destino',
	'warehouse_destination_id': 'Bodega de destino',
	'warehouse_origin_id': 'Bodega de origen',
	'pick_person_id': 'Persona que retira',

	// ResiduosDestinoSection
	'residue_type_id': 'Tipo de residuo',
	'destiny_type_id': 'Tipo de destino',
	'warehouse_id': 'Bodega',
	'final_destination_id': 'Destino final',

	// TransporteSection
	'transfer_patente_id': 'Patente',
	'patente_id': 'Patente',
	'transfer_conductor_rut': 'RUT conductor',
	'transfer_category_ids': 'Categoría de transporte',

	// ItemsSection
	'items': 'Ítems',

	// TotalesSection
	'totals_mnt_neto': 'Monto neto',
	'totals_iva': 'IVA',
	'totals_mnt_total': 'Monto total',

	// Observaciones
	'observaciones_guia': 'Observaciones',
});

/**
 * Helper para obtener label de campo
 * @param {string} fieldName - Nombre del campo
 * @returns {string} Label amigable o el nombre del campo si no existe
 */
export const getFieldLabel = (fieldName) => {
	return FIELD_LABELS[fieldName] || fieldName;
};

/**
 * Rutas de carpetas para almacenamiento de documentos
 */
export const FOLDER_PATHS = Object.freeze({
	EPP_RETIRO: '/Abastecimiento/EPP/Retiro',
	EPP_TRANSFERENCIA: '/Abastecimiento/EPP/Transferencia',
	RESIDUOS: '/Residuos/Guias',
	RESIDUOS_CONSOLIDADAS: '/Residuos/Guias/Consolidadas',
});

/**
 * Códigos de tipo DTE (Documento Tributario Electrónico)
 */
export const DTE_TYPE_CODES = Object.freeze({
	GUIA_DESPACHO: 52,
	FACTURA: 33,
	NOTA_CREDITO: 61,
	NOTA_DEBITO: 56,
});

/**
 * Servicios DTE disponibles
 */
export const DTE_SERVICES = Object.freeze({
	FACELE: 'facele',
	GBUSINESS: 'gbusiness',
	DBNET: 'dbnet',
});
